import React, { useState, useRef } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { UserMentionInput } from '../ui/UserMentionInput';
import { GifIcon } from '../ui/GifIcon';
import { GifPicker } from '../ui/GifPicker';
import { supabase } from '../../lib/supabase';
import { uploadMedia } from '../../lib/storage';
import { toast } from 'sonner';

const MAX_REPLY_MEDIA = 4;

export function ConversationReplyForm({
  postId,
  parentId,
  replyToUsername,
  replyToUserId,
  onSuccess,
  onCancel
}: {
  postId: string;
  parentId: string;
  replyToUsername?: string;
  replyToUserId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [content, setContent] = useState('');
  const [gifUrls, setGifUrls] = useState<string[]>([]);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase.from('users').select('username, avatar_url').eq('id', user.id).single();
        setCurrentUser({ id: user.id, username: profile?.username, avatar_url: profile?.avatar_url });
      }
    };
    getUser();
    if (replyToUsername && replyToUserId) {
      setContent(`@${replyToUsername} `);
      setMentionedUsers([replyToUserId]);
    }
  }, [replyToUsername, replyToUserId]);

  const totalMedia = gifUrls.length + mediaFiles.length;
  const canAddMedia = totalMedia < MAX_REPLY_MEDIA;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    if (files.length + totalMedia > MAX_REPLY_MEDIA) {
      toast.error(`Max ${MAX_REPLY_MEDIA} media`);
      return;
    }
    setMediaFiles((p) => [...p, ...files]);
    e.target.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!content.trim() && gifUrls.length === 0 && mediaFiles.length === 0) || !currentUser) return;

    setLoading(true);
    try {
      let mediaUrls: string[] = [...gifUrls];
      if (mediaFiles.length > 0) {
        const uploaded = await Promise.all(
          mediaFiles.map((f) => {
            const bucket = f.type.startsWith('video/') ? 'post-videos' : 'post-images';
            return uploadMedia(f, bucket);
          })
        );
        mediaUrls = [...mediaUrls, ...uploaded.filter(Boolean) as string[]];
      }

      const { error } = await supabase.from('conversation_comments').insert([{
        post_id: postId,
        parent_id: parentId,
        content: content.trim() || '',
        images: mediaUrls,
        user_id: currentUser.id
      }]);

      if (error) throw error;

      setContent('');
      setGifUrls([]);
      setMediaFiles([]);
      setMentionedUsers([]);
      onSuccess();
    } catch (err: any) {
      console.error('Error adding reply:', err);
      toast.error(err.message || 'Failed to reply');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
        <p className="text-sm text-gray-600 mb-2">Log in to join the conversation</p>
        <button
          onClick={() => {
            sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
            window.dispatchEvent(new CustomEvent('show-auth'));
          }}
          className="px-4 py-2 text-sm font-medium text-white bg-[#F49F0D] rounded-lg hover:bg-[#F49F0D]/90 transition-colors"
        >
          Log In
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg">
      <UserMentionInput
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onMentionedUsersChange={setMentionedUsers}
        placeholder="Write a reply..."
        className="w-full bg-transparent border-0 rounded-lg px-3 py-3 text-sm focus:outline-none focus:ring-0 resize-none min-h-[44px]"
        rows={1}
      />

      {(gifUrls.length > 0 || mediaFiles.length > 0) && (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2">
          {gifUrls.map((url, i) => (
            <div key={i} className="relative">
              <img src={url} alt="GIF" className="h-12 w-12 object-cover rounded" />
              <button
                type="button"
                onClick={() => setGifUrls((p) => p.filter((_, j) => j !== i))}
                className="absolute -top-0.5 -right-0.5 bg-red-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[10px]"
              >
                ×
              </button>
            </div>
          ))}
          {mediaFiles.map((file, i) => (
            <div key={i} className="relative">
              {file.type.startsWith('video/') ? (
                <video src={URL.createObjectURL(file)} className="h-12 w-12 object-cover rounded" muted loop />
              ) : (
                <img src={URL.createObjectURL(file)} alt="" className="h-12 w-12 object-cover rounded" />
              )}
              <button
                type="button"
                onClick={() => setMediaFiles((p) => p.filter((_, j) => j !== i))}
                className="absolute -top-0.5 -right-0.5 bg-red-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[10px]"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between px-3 pb-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowGifPicker(true)}
            disabled={!canAddMedia}
            className="p-1.5 text-gray-500 hover:text-accent-text hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
            title="Add GIF"
          >
            <GifIcon className="h-4 w-4 text-current" />
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={!canAddMedia}
            className="p-1.5 text-gray-500 hover:text-accent-text hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
            title="Add photo or video"
          >
            <ImageIcon className="h-4 w-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-2 text-xs text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || (!content.trim() && gifUrls.length === 0 && mediaFiles.length === 0)}
            className="px-4 py-2 text-xs font-medium text-white bg-accent-text rounded-lg hover:bg-accent-text/90 disabled:opacity-50"
          >
            {loading ? 'Posting...' : 'Reply'}
          </button>
        </div>
      </div>

      <GifPicker
        isOpen={showGifPicker}
        onClose={() => setShowGifPicker(false)}
        onSelect={(url) => canAddMedia && setGifUrls((p) => [...p, url])}
      />
    </form>
  );
}
